import {
  NpCargoType,
  NpCity,
  NpCodType,
  NpDeliveryType,
  NpPayer,
  NpPaymentMethod,
  NpPrintFormat,
  NpShipmentStatus,
} from './nova-poshta.types';

export const NP_CABINET_NAME_MAX_LENGTH = 60;
/** Nova Poshta takes up to 120 characters */
export const NP_DESCRIPTION_MAX_LENGTH = 120;
export const NP_NOTE_MAX_LENGTH = 100;
/** Nova Poshta takes Ukrainian mobile numbers only */
export const NP_PHONE_PATTERN = /^\+?380\d{9}$/;

export interface NpOption<T extends string> {
  value: T;
  /** A key of `novaposhta` */
  label: string;
  icon?: string;
}

export const NP_DELIVERY_TYPES: NpOption<NpDeliveryType>[] = [
  { value: 'warehouse', label: 'novaposhta.delivery_type.warehouse', icon: 'store' },
  { value: 'postomat', label: 'novaposhta.delivery_type.postomat', icon: 'inventory_2' },
  { value: 'address', label: 'novaposhta.delivery_type.address', icon: 'home' },
];

export const NP_CARGO_TYPES: NpOption<NpCargoType>[] = [
  { value: 'Parcel', label: 'novaposhta.cargo_type.Parcel', icon: 'package_2' },
  { value: 'Documents', label: 'novaposhta.cargo_type.Documents', icon: 'description' },
  { value: 'Cargo', label: 'novaposhta.cargo_type.Cargo', icon: 'deployed_code' },
  { value: 'Pallet', label: 'novaposhta.cargo_type.Pallet', icon: 'pallet' },
  { value: 'TiresWheels', label: 'novaposhta.cargo_type.TiresWheels', icon: 'tire_repair' },
];

export const NP_PAYERS: NpOption<NpPayer>[] = [
  { value: 'Recipient', label: 'novaposhta.payer.Recipient' },
  { value: 'Sender', label: 'novaposhta.payer.Sender' },
];

export const NP_PAYMENT_METHODS: NpOption<NpPaymentMethod>[] = [
  { value: 'Cash', label: 'novaposhta.payment_method.Cash' },
  { value: 'NonCash', label: 'novaposhta.payment_method.NonCash' },
];

export const NP_COD_TYPES: NpOption<NpCodType>[] = [
  { value: 'none', label: 'novaposhta.cod_type.none' },
  { value: 'money_transfer', label: 'novaposhta.cod_type.money_transfer' },
  { value: 'payment_control', label: 'novaposhta.cod_type.payment_control' },
];

export const NP_PRINT_FORMATS: NpOption<NpPrintFormat>[] = [
  { value: 'waybill', label: 'novaposhta.print.waybill', icon: 'receipt_long' },
  { value: 'marking_85x85', label: 'novaposhta.print.marking_85x85', icon: 'qr_code_2' },
  { value: 'marking_100x100', label: 'novaposhta.print.marking_100x100', icon: 'qr_code_2' },
];

/** Badge and icon of a waybill status */
export const NP_SHIPMENT_STATUS_STYLES: Record<NpShipmentStatus, { badge: string; icon: string }> = {
  created: { badge: 'bg-slate-100 text-slate-600', icon: 'edit_note' },
  in_transit: { badge: 'bg-sky-50 text-sky-700', icon: 'local_shipping' },
  arrived: { badge: 'bg-amber-50 text-amber-700', icon: 'store' },
  received: { badge: 'bg-emerald-50 text-emerald-700', icon: 'task_alt' },
  refused: { badge: 'bg-rose-50 text-rose-700', icon: 'block' },
  returned: { badge: 'bg-violet-50 text-violet-700', icon: 'undo' },
  deleted: { badge: 'bg-slate-100 text-slate-400', icon: 'delete' },
};

/** A Nova Poshta API key is 32 hex characters */
export const isNpApiKeyFormat = (key: string): boolean => /^[a-f0-9]{32}$/i.test(key.trim());

/** A Nova Poshta waybill number: 14 digits; spaces are allowed */
export const isTtnFormat = (value: string): boolean => /^\d{14}$/.test(value.replace(/\s/g, ''));

/** «+380671234567» → «+38 067 123 45 67» */
export function formatNpPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length !== 12) return phone;
  return `+${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 10)} ${digits.slice(10)}`;
}

/** «20450123456789» → «2045 0123 4567 89», the way Nova Poshta prints it */
export function formatTtn(ttn: string): string {
  return ttn.replace(/(\d{4})(?=\d)/g, '$1 ');
}

/** «+38 (067) 123-45-67», «0671234567» → «380671234567» */
export function normalizeNpPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10 && digits.startsWith('0')) return `38${digits}`;
  return digits;
}

/** Volumetric weight of one seat, kg: Nova Poshta charges the bigger one of it and the real weight */
export function getVolumetricWeight(length: number | null, width: number | null, height: number | null): number {
  if (!length || !width || !height) return 0;
  return Math.round(((length * width * height) / 4000) * 100) / 100;
}

export interface NpCityOption extends NpCity {
  /** «Біла Церква, Київська обл., Білоцерківський р-н» */
  label: string;
}

export const toNpCityOption = (city: NpCity): NpCityOption => ({
  ...city,
  label: city.area ? `${city.name}, ${city.area}` : city.name,
});

/** Search results with the picked option kept in: the select shows only an option it has */
export const withPicked = <T extends { ref: string }>(options: T[], picked: T | null): T[] =>
  picked && !options.some((option) => option.ref === picked.ref) ? [picked, ...options] : options;
